import type { ISODate, TaskId } from '@interfaces/IScheduler';
import { isScheduled } from './taskDates';

/** The span of a phase none of whose leaves is dated. */
const UNSCHEDULED: { start: ISODate; end: ISODate } = { start: '', end: '' };

// ---------------------------------------------------------------------------
// WBS roll-up: a summary task has no dates of its own, only the span its
// descendant leaves occupy (min start / max end). Every reader of the task
// graph that follows a dependency needs the same answer — the cascade, and the
// check for a link the dates no longer satisfy — so the walk lives here once.
// ---------------------------------------------------------------------------

export interface IRollupNode {
  id: TaskId;
  parentId?: TaskId | null;
  start: ISODate;
  end: ISODate;
}

/** Parent → child ids. A task absent from the map is a LEAF. */
export function childIndex(tasks: Iterable<IRollupNode>): Map<TaskId, TaskId[]> {
  const childIds = new Map<TaskId, TaskId[]>();
  for (const task of tasks) {
    if (task.parentId == null) continue;
    const siblings = childIds.get(task.parentId);
    if (siblings) siblings.push(task.id);
    else childIds.set(task.parentId, [task.id]);
  }
  return childIds;
}

export interface IRollup {
  effStart: (id: TaskId) => ISODate;
  effEnd: (id: TaskId) => ISODate;
  /** Leaf `id`'s dates just changed: forget its ancestors' cached spans. */
  leafMoved: (id: TaskId) => void;
}

/**
 * Effective (rolled-up) span of a node. A summary's span is computed once and
 * cached, so a cascade that reads the same phase from many edges pays for its
 * subtree once rather than once per edge. The cache still reflects the current
 * pass: a caller that moves a leaf reports it through `leafMoved`, which drops
 * the spans of every summary above it. `get` is the indirection that lets one
 * caller read the working clones and another the untouched originals over the
 * same child index.
 */
export function makeRollup(
  childIds: ReadonlyMap<TaskId, readonly TaskId[]>,
  get: (id: TaskId) => IRollupNode,
): IRollup {
  const spans = new Map<TaskId, { start: ISODate; end: ISODate }>();

  function span(id: TaskId): { start: ISODate; end: ISODate } {
    const kids = childIds.get(id);
    if (!kids) return get(id);
    const cached = spans.get(id);
    if (cached) return cached;
    // Only dated children have a span to contribute: an unscheduled one (no
    // dates, `''`) would otherwise win every `min`. A phase with no dated child
    // at all is itself unscheduled.
    const rolled = kids
      .map(span)
      .filter(isScheduled)
      .reduce<{ start: ISODate; end: ISODate } | null>((acc, kid) => (acc === null ? { start: kid.start, end: kid.end } : {
        start: kid.start < acc.start ? kid.start : acc.start,
        end: kid.end > acc.end ? kid.end : acc.end,
      }), null) ?? UNSCHEDULED;
    spans.set(id, rolled);
    return rolled;
  }

  return {
    effStart: (id) => span(id).start,
    effEnd: (id) => span(id).end,
    leafMoved: (id) => {
      // A span is built from its children's, so a cached summary has cached
      // summaries all the way down — the first uncached ancestor ends the walk.
      // Callers only ask for tasks that exist, so a parent id with no task
      // behind it is never cached and the walk never has to `get` it.
      let parent = get(id).parentId;
      while (parent != null && spans.delete(parent)) parent = get(parent).parentId;
    },
  };
}
